import DisclosureToggle from 'tio-ui/components/disclosure-toggle';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { Section } from 'tio-ui/components/layout';
import Text from 'tio-ui/components/text';
import type { AnswerProfileExtension as AnswerProfileModel } from 'tio-data/data/resources/answer-profile.ext';
import type { IntlService } from 'ember-intl';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type { TemplateOnlyComponent } from '@ember/component/template-only';
import type { Store } from '@warp-drive/core';
import { saveRecord } from 'tio-data/requests/save-record';
import { PhArrowCircleRight, PhCheckCircle } from 'ember-phosphor-icons';

export interface PslfRecommendationsSignature {
  Args: {
    loanSummary: LoanSummary;
    person: PersonModel;
    recommendation?: RecommendationModel;
    debugLinkLoans?: boolean;
    answerProfile?: AnswerProfileModel;
  };
  Element: HTMLElement;
}

interface RecommendationItem {
  id: string;
  titleKey: string;
  descriptionKey: string;
}

/**
 * Self-toggle button for marking an external recommendation complete /
 * incomplete. Renders a leading check icon + label that swaps based on
 * the checked state. Co-located here because only this file consumes
 * it (3 call sites — consolidate / changeRepaymentPlan / recertify).
 */
interface MarkCompleteButtonSignature {
  Args: {
    isChecked: boolean;
    onToggle: () => void;
    testId: string;
  };
}

const MarkCompleteButton: TemplateOnlyComponent<MarkCompleteButtonSignature> = <template>
  <button
    type="button"
    class="app-mark-complete-btn {{if @isChecked 'app-mark-complete-btn--checked'}}"
    data-test-pslf-mark-complete={{@testId}}
    {{on "click" @onToggle}}
  >
    {{#if @isChecked}}
      <PhCheckCircle @size={{16}} @weight="fill" />
      {{t "pslf.reimagined.recommendations.mark_incomplete"}}
    {{else}}
      <PhCheckCircle @size={{16}} />
      {{t "pslf.reimagined.recommendations.mark_complete"}}
    {{/if}}
  </button>
</template>;

/**
 * "Description + external action link + mark-complete toggle" — the
 * full body for an external recommendation (consolidate / change
 * repayment / recertify income). Co-located here (3 call sites).
 */
interface ExternalRecBodySignature {
  Args: {
    description: string;
    linkHref: string;
    linkLabel: string;
    isChecked: boolean;
    onToggle: () => void;
    testId: string;
  };
}

const ExternalRecBody: TemplateOnlyComponent<ExternalRecBodySignature> = <template>
  <Text @variant="muted" @size="sm">
    {{@description}}
  </Text>
  <a
    href={{@linkHref}}
    target="_blank"
    rel="noreferrer noopener"
    class="text-sm mt-2 app-text-link app-text-link--inline app-text-link--icon"
  >
    {{@linkLabel}}
    <PhArrowCircleRight @size={{14}} />
  </a>
  <div class="mt-3 pt-3 border-t app-section-divider">
    <MarkCompleteButton @isChecked={{@isChecked}} @onToggle={{@onToggle}} @testId={{@testId}} />
  </div>
</template>;

/**
 * "Description + upload-NSLDS link" — body for an in-platform
 * recommendation (provideRepaymentPlan / linkLoans). Co-located.
 */
interface InPlatformRecBodySignature {
  Args: {
    description: string;
    testId: string;
  };
}

const InPlatformRecBody: TemplateOnlyComponent<InPlatformRecBodySignature> = <template>
  <Text @variant="muted" @size="sm" class="mb-3">
    {{@description}}
  </Text>
  <LinkTo
    @route="authenticated.observability.upload"
    @query={{hash source="pslf" providers="nslds,statement"}}
    class="text-sm app-text-link app-text-link--inline app-text-link--icon"
    data-test-pslf-upload-nslds={{@testId}}
  >
    {{t "pslf.reimagined.recommendations.upload_nslds"}}
    <PhArrowCircleRight @size={{14}} />
  </LinkTo>
</template>;

export default class PslfRecommendations extends Component<PslfRecommendationsSignature> {
  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked expandedId: string | null = null;

  get answerProfile() {
    return this.args.answerProfile;
  }

  get loanSummariesByType() {
    return this.args.loanSummary?.summaryByLoanType || [];
  }

  get shouldConsolidateLoans() {
    return !!this.loanSummariesByType.find((summary) => {
      return summary.loanType !== 'direct';
    });
  }

  get hasMissingRepaymentPlan() {
    const allRepaymentPlans = this.loanSummariesByType.flatMap((summary) => summary.repaymentPlans);
    return allRepaymentPlans.some((plan) => plan === 'noRepaymentPlan' || !plan);
  }

  get shouldChangeRepaymentPlan() {
    if (this.hasMissingRepaymentPlan || this.loanSummariesByType.length === 0) {
      return false;
    }
    const validRepaymentPlansForPslf = ['icr', 'ibr', 'paye', 'repaye', 'fivePercentUndergrad'];
    const allRepaymentPlans = [
      ...new Set(this.loanSummariesByType.flatMap((summary) => summary.repaymentPlans)),
    ];
    return (
      allRepaymentPlans.length > 0 &&
      allRepaymentPlans.every((plan) => !validRepaymentPlansForPslf.includes(plan))
    );
  }

  get hasNsldsData() {
    return (this.args.person.accounts ?? []).some((account) => account.hasNsldsPslfData);
  }

  get shouldLinkLoans() {
    if (this.args.debugLinkLoans) return true;
    return !this.hasNsldsData;
  }

  get needsToRecertify() {
    return !!this.args.recommendation;
  }

  // Whether external recs are checked off by the user
  get isConsolidationChecked() {
    return !!this.answerProfile?.consolidatedLoansAt;
  }

  get isRepaymentPlanChecked() {
    return !!this.answerProfile?.changedRepaymentPlanAt;
  }

  get isRecertifyIncomeChecked() {
    return !!this.answerProfile?.recertifiedIncomeAt;
  }

  isCheckedOff = (id: string): boolean => {
    if (id === 'consolidate') return this.isConsolidationChecked;
    if (id === 'changeRepaymentPlan') return this.isRepaymentPlanChecked;
    if (id === 'recertifyIncome') return this.isRecertifyIncomeChecked;
    return false;
  };

  get allRecommendations(): RecommendationItem[] {
    const items: RecommendationItem[] = [];

    if (this.shouldConsolidateLoans) {
      items.push({
        id: 'consolidate',
        titleKey: 'pslf.reimagined.recommendations.consolidate_loans.title',
        descriptionKey: 'pslf.reimagined.recommendations.consolidate_loans.description',
      });
    }

    if (this.hasMissingRepaymentPlan) {
      items.push({
        id: 'provideRepaymentPlan',
        titleKey: 'pslf.reimagined.recommendations.provide_repayment_plan.title',
        descriptionKey: 'pslf.reimagined.recommendations.provide_repayment_plan.description',
      });
    }

    if (this.shouldChangeRepaymentPlan) {
      items.push({
        id: 'changeRepaymentPlan',
        titleKey: 'pslf.reimagined.recommendations.change_repayment_plan.title',
        descriptionKey: 'pslf.reimagined.recommendations.change_repayment_plan.description',
      });
    }

    if (this.shouldLinkLoans) {
      items.push({
        id: 'linkLoans',
        titleKey: 'pslf.reimagined.recommendations.link_loans.title',
        descriptionKey: 'pslf.reimagined.recommendations.link_loans.description',
      });
    }

    if (this.needsToRecertify) {
      items.push({
        id: 'recertifyIncome',
        titleKey: 'pslf.reimagined.recommendations.certify_income.title',
        descriptionKey: 'pslf.reimagined.recommendations.certify_income.description',
      });
    }

    return items;
  }

  get activeRecommendationCount() {
    return this.allRecommendations.filter((rec) => !this.isCheckedOff(rec.id)).length;
  }

  get hasNoRecommendations() {
    return this.allRecommendations.length === 0;
  }

  get headingWithCount() {
    return `${this.intl.t('pslf.reimagined.recommendations.heading')} (${this.activeRecommendationCount})`;
  }

  toggleItem = (id: string) => {
    this.expandedId = this.expandedId === id ? null : id;
  };

  toggleConsolidation = async () => {
    const profile = this.answerProfile;
    if (!profile) return;
    try {
      profile.consolidatedLoansAt = profile.consolidatedLoansAt ? '' : new Date().toISOString();
      await saveRecord(this.store, profile);
    } catch (e) {
      console.error(e);
      profile.rollbackAttributes();
    }
  };

  toggleRepaymentPlan = async () => {
    const profile = this.answerProfile;
    if (!profile) return;
    try {
      profile.changedRepaymentPlanAt = profile.changedRepaymentPlanAt
        ? ''
        : new Date().toISOString();
      await saveRecord(this.store, profile);
    } catch (e) {
      console.error(e);
      profile.rollbackAttributes();
    }
  };

  toggleRecertifyIncome = async () => {
    const profile = this.answerProfile;
    if (!profile) return;
    try {
      profile.recertifiedIncomeAt = profile.recertifiedIncomeAt ? '' : new Date().toISOString();
      await saveRecord(this.store, profile);
    } catch (e) {
      console.error(e);
      profile.rollbackAttributes();
    }
  };

  <template>
    {{#unless this.hasNoRecommendations}}
      <Section data-test-pslf-recommendations ...attributes>
        <:header>
          {{this.headingWithCount}}
        </:header>
        <:subHeader>
          {{t "pslf.reimagined.recommendations.subtitle"}}
        </:subHeader>
        <:body>
          <div class="space-y-2">
            {{#each this.allRecommendations as |rec|}}
              <div
                data-test-recommendation-item={{rec.id}}
                class="app-rec-item {{if (this.isCheckedOff rec.id) 'app-rec-item--checked'}}"
              >
                <button
                  type="button"
                  aria-expanded={{if (eq this.expandedId rec.id) "true" "false"}}
                  class="w-full px-4 py-3 tio-row text-left app-row-hover"
                  data-test-pslf-recommendation-toggle={{rec.id}}
                  {{on "click" (fn this.toggleItem rec.id)}}
                >
                  <div
                    class="app-tinted-circle
                      {{if
                        (this.isCheckedOff rec.id)
                        'app-tinted-circle--success'
                        'app-tinted-circle--neutral'
                      }}"
                  >
                    {{#if (this.isCheckedOff rec.id)}}
                      <PhCheckCircle @size={{16}} @weight="fill" />
                    {{else}}
                      <PhArrowCircleRight @size={{16}} />
                    {{/if}}
                  </div>
                  <div class="tio-fill-min">
                    <Text
                      @variant="inherit"
                      @size="sm"
                      @weight="semibold"
                      class="{{if
                          (this.isCheckedOff rec.id)
                          'line-through app-text-disabled'
                          'app-text-primary'
                        }}"
                    >
                      {{t rec.titleKey}}
                    </Text>
                    <Text @variant="muted" @size="sm" class="truncate">
                      {{t rec.descriptionKey}}
                    </Text>
                  </div>
                  <DisclosureToggle
                    class="shrink-0 app-text-disabled"
                    @expanded={{eq this.expandedId rec.id}}
                  />
                </button>

                {{#if (eq this.expandedId rec.id)}}
                  <div class="px-4 pb-4 pt-1 border-t app-section-divider">
                    {{#if (eq rec.id "provideRepaymentPlan")}}
                      <InPlatformRecBody
                        @description={{t "recommendations.provide_repayment_plan_description"}}
                        @testId="provideRepaymentPlan"
                      />
                    {{/if}}

                    {{#if (eq rec.id "linkLoans")}}
                      <InPlatformRecBody
                        @description={{t "recommendations.please_add_accounts"}}
                        @testId="linkLoans"
                      />
                    {{/if}}

                    {{#if (eq rec.id "consolidate")}}
                      <ExternalRecBody
                        @description={{t "recommendations.required"}}
                        @linkHref="https://studentaid.gov/app/launchConsolidation.action"
                        @linkLabel={{t "pslf.reimagined.recommendations.consolidate_loans.link"}}
                        @isChecked={{this.isConsolidationChecked}}
                        @onToggle={{this.toggleConsolidation}}
                        @testId="consolidation"
                      />
                    {{/if}}

                    {{#if (eq rec.id "changeRepaymentPlan")}}
                      <ExternalRecBody
                        @description={{t "recommendations.change_repayment_plan_pslf"}}
                        @linkHref="https://studentaid.gov/manage-loans/repayment/plans"
                        @linkLabel={{t
                          "pslf.reimagined.recommendations.change_repayment_plan.link"
                        }}
                        @isChecked={{this.isRepaymentPlanChecked}}
                        @onToggle={{this.toggleRepaymentPlan}}
                        @testId="repayment-plan"
                      />
                    {{/if}}

                    {{#if (eq rec.id "recertifyIncome")}}
                      <ExternalRecBody
                        @description={{t "recommendations.need_recertify_income"}}
                        @linkHref="https://studentaid.gov/idr/"
                        @linkLabel={{t "recommendations.recertify_income"}}
                        @isChecked={{this.isRecertifyIncomeChecked}}
                        @onToggle={{this.toggleRecertifyIncome}}
                        @testId="recertify-income"
                      />
                    {{/if}}
                  </div>
                {{/if}}
              </div>
            {{/each}}
          </div>
        </:body>
      </Section>
    {{/unless}}
  </template>
}
